import debounce from "lodash.debounce";

import mutations from "../../../shared/helpers/mutations";

import FrontendAPI from "../../api/search";

const API = new FrontendAPI();
const DEBOUNCE_TIMEOUT = 250;
const ICONS = {
  "google-place": "map-marker",
  region: "map-marker",
  tour: "map-o",
  tournament: "trophy",
  venue: "flag"
};
const KEYCODES = {
  TAB: 9,
  ENTER: 13,
  ESC: 27,
  UP: 38,
  DOWN: 40
};
const MINLENGTH = 2;
const SELECTOR = "[data-component^=autocomplete-]:not([data-enhanced])";
const TRANS = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const NEW_ICON = document.createElement("i");

NEW_ICON.classList.add("fa");
NEW_ICON.setAttribute("aria-hidden", "true");

const NEW_IMAGE = document.createElement("img");

NEW_IMAGE.src = TRANS;
NEW_IMAGE.setAttribute("aria-hidden", "true");
NEW_IMAGE.setAttribute("height", "1");
NEW_IMAGE.setAttribute("width", "1");

const NEW_LINK = document.createElement("a");

NEW_LINK.classList.add("gtm-track-click");
NEW_LINK.href = "#";

function getCategory(input) {
  const isHomePage = location.path === "/";
  const inHeader = !!input.closest("header");
  let place = inHeader ? "Header" : "Body";

  if (isHomePage && !inHeader) {
    place = "Home page";
  }

  return `Autocomplete - ${place} search`;
}

async function onprefetch(input, e) {
  e.preventDefault();
  const { target } = e;

  target.removeEventListener("click", target.prefetch);
  delete target.prefetch;

  const {
    bounding_box_url: boundingBox,
    error,
    formatted_address: address
  } = await API.googlePlace(this);

  if (!error) {
    const href = `/place/${encodeURIComponent(address)}/${encodeURIComponent(boundingBox)}`;
    const { dataset } = target;

    dataset.gtmCategory = getCategory(input);
    target.href = href;
    target.dispatchEvent(new MouseEvent("click")); // re-click link
  }
}

function addResult(input, option) {
  const { cc, prefetch, type, value } = option.dataset;
  const { textContent } = option;
  const a = NEW_LINK.cloneNode();
  const { dataset } = a;

  if (prefetch) {
    a.prefetch = onprefetch.bind(value, input);
    a.addEventListener("click", a.prefetch);
  } else {
    a.href = value;

    // due to the "global" click event handler, we don't want GTMCategory on prefetch links
    // otherwise we get an extra entry in window.dataLayer from `trackClick`
    dataset.gtmCategory = getCategory(input);
  }

  dataset.gtmAction = textContent;

  if (type === "region" && cc) {
    const img = NEW_IMAGE.cloneNode();

    img.alt = `Flag of ${textContent}`;
    img.classList.add(`sprite_flags_${cc}`);

    a.appendChild(img);
  } else {
    const icon = NEW_ICON.cloneNode();

    icon.classList.add(`fa-${ICONS[type]}`);

    a.appendChild(icon);
  }

  a.appendChild(document.createTextNode(textContent));
  const li = document.createElement("li");

  li.appendChild(a);
  this.appendChild(li);
}

function getDetails(target) {
  return target.closest("section").querySelector("details");
}

function fillDetails(input) {
  const { list } = input.dataset;
  const datalist = document.getElementById(list);
  const details = getDetails(input);
  const { lastChild } = details;

  if (lastChild.nodeName === "UL") {
    details.removeChild(lastChild);
  }

  if (datalist) {
    const ul = document.createElement("ul");
    const fragment = document.createDocumentFragment();

    datalist.querySelectorAll("option").forEach(addResult.bind(fragment, input));
    ul.appendChild(fragment);

    details.appendChild(ul);
  } else {
    // eslint-disable-next-line
    console.warn(`Unable to find autocomplete list #${list}`);
  }
}

function fillItem({ countryCode, name, reference, slug, type }) {
  const option = document.createElement("option");
  const { dataset } = option;

  if (countryCode) {
    dataset.cc = countryCode;
  }

  dataset.type = type;
  if (type === "google-place") {
    dataset.value = reference;
    dataset.prefetch = "googlePlace";
  } else {
    dataset.value = `/${slug}`;
  }

  option.textContent = name;

  this.appendChild(option);
}

function fillList(input, results) {
  const { initial, list } = input.dataset;

  if (list && list !== initial) {
    const fragment = document.createDocumentFragment();

    results.forEach(fillItem.bind(fragment));

    const datalist = document.createElement("datalist");

    datalist.id = list;
    datalist.appendChild(fragment);

    const initialList = document.getElementById(initial) || document.body.lastChild;

    initialList.parentNode.appendChild(datalist);
  }
}

function onclick(e) {
  const details = getDetails(e.target);
  const fn = details.open ? "removeAttribute" : "setAttribute";

  details[fn]("open", "");
}

function ondetailsclick(e) {
  const { target } = e;

  if (target.prefetch) {
    e.preventDefault();
  }

  target.dataset.gtmLabel = this.value;
}

function ondetailskeydown(e) {
  const { keyCode } = e;
  let { target } = e;

  if (keyCode !== KEYCODES.ENTER) {
    e.preventDefault();

    const input = this;
    const { nextSibling, previousSibling } = target.parentNode;

    if ((keyCode === KEYCODES.TAB && !nextSibling) || keyCode === KEYCODES.ESC) {
      input.dispatchEvent(new MouseEvent("click"));

      return;
    }

    if (keyCode === KEYCODES.DOWN) {
      // next entry or wrap round to first
      target = (nextSibling || target.closest("ul")).querySelector("a");
    }

    if (keyCode === KEYCODES.UP) {
      // previous entry or jump up to input
      target = previousSibling ? previousSibling.querySelector("a") : input;
    }

    target.focus();
  }
}

function ondocumentclick(e) {
  const self = this;

  if (!self.contains(e.target)) {
    getDetails(self).removeAttribute("open");
  }
}

async function oninputinput(e) {
  const { target: input } = e;
  const { value } = input;
  const { results = [], total = 0 } = value.length > MINLENGTH ? await API.autocomplete(value) : {};
  const { initial, list } = input.dataset;

  if (list && list !== initial) {
    const datalist = document.getElementById(list);

    datalist.parentNode.removeChild(datalist);
  }

  input.dataset.list = total ? `autocomplete-${Math.random().toString(36).substr(2)}` : initial;
  fillList(input, results);
  fillDetails(input);

  const details = getDetails(input);

  if (!details.open) {
    input.dispatchEvent(new MouseEvent("click"));
  }
}

function oninputkeydown(e) {
  const { altKey, keyCode, shiftKey, target } = e;
  const details = getDetails(target);
  const { open } = details;
  const enter = keyCode === KEYCODES.ENTER;
  const tab = keyCode === KEYCODES.TAB;
  const down = (tab && altKey) || keyCode === KEYCODES.DOWN;
  const up = (tab && shiftKey) || keyCode === KEYCODES.UP || keyCode === KEYCODES.ESC;
  const toggle = open ? up : down;

  if (toggle) {
    target.dispatchEvent(new MouseEvent("click"));
  }

  if (down && open) {
    e.preventDefault();

    details.querySelector("a").focus();
  }

  if (enter && open) {
    if (target.dataset.initial !== target.dataset.list) {
      getDetails(target).querySelector('a').dispatchEvent(new MouseEvent("click"));
    }
  }
}

function processAutocomplete(autocomplete) {
  const input = autocomplete.querySelector("[type=search]");
  const list = input.getAttribute("list");

  if (list) {
    const { dataset } = input;

    dataset.initial = list; // save initial list ref
    dataset.list = dataset.initial; // "working" list
    input.removeAttribute("list"); // suppress built-in autocomplete
  }

  const details = document.createElement("details");

  details.appendChild(document.createElement("summary"));
  input.closest("form").appendChild(details);

  fillDetails(input);

  input.addEventListener("click", onclick);
  input.addEventListener("input", debounce(oninputinput, DEBOUNCE_TIMEOUT));
  input.addEventListener("keydown", oninputkeydown);
  input.form.addEventListener("submit", (e) => e.preventDefault());

  details.addEventListener("click", ondetailsclick.bind(input));
  details.addEventListener("keydown", ondetailskeydown.bind(input));

  document.addEventListener("click", ondocumentclick.bind(input.form));

  autocomplete.dataset.enhanced = "";
}

function init() {
  document.querySelectorAll(SELECTOR).forEach(processAutocomplete);
}

document.addEventListener("DOMContentLoaded", init);

mutations.add(SELECTOR, processAutocomplete);



// WEBPACK FOOTER //
// ./scripts/sections/blocks/autocomplete.js